import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Briefcase, Loader2 } from "lucide-react";

const HERO = "https://images.pexels.com/photos/18153132/pexels-photo-18153132.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function DeveloperLogin() {
  const { login, logout, scopeInto } = useAuth();
  const navigate = useNavigate();
  const [ident, setIdent] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(ident, password);
      if (u.role !== "developer") {
        await logout();
        setError("These aren't developer credentials. Please use the right portal.");
        return;
      }
      scopeInto(null);
      navigate("/developer/societies");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="flex w-full flex-col justify-center px-8 md:w-1/2 md:px-16 lg:px-24">
        <div className="mx-auto w-full max-w-sm">
          <img src="/propto-logo.png" alt="Propto" className="mb-6 h-16 w-auto object-contain" />
          <span className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-medium text-brand">
            <Briefcase className="h-3.5 w-3.5" /> Developer
          </span>
          <h1 className="mb-2 font-cabinet text-4xl font-bold tracking-tight text-brand">Developer Portal</h1>
          <p className="mb-8 text-sm text-zinc-500">Sign in to set up and manage your societies until handover.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="ident">Username or email</Label>
              <Input id="ident" data-testid="dev-login-ident-input" value={ident} onChange={(e) => setIdent(e.target.value)}
                placeholder="your-username" className="mt-1.5" required autoComplete="username" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="dev-login-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" required />
            </div>
            {error && <p data-testid="dev-login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
            <Button data-testid="dev-login-submit-button" type="submit" disabled={loading} className="w-full">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Sign in
            </Button>
          </form>
        </div>
      </div>
      <div className="relative hidden w-1/2 md:block">
        <img src={HERO} alt="Residential project" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-brand-green/55" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="mb-4 h-1 w-16 rounded-full bg-brand-gold" />
          <p className="font-cabinet text-3xl font-bold leading-tight tracking-tight">Build it, run it, hand it over.</p>
          <p className="mt-3 max-w-md text-sm text-white/80">Set up every society you deliver, then hand it to its committee in one step.</p>
        </div>
      </div>
    </div>
  );
}
